import { useEffect, useRef, useState } from 'react';
import { Check, Search } from 'lucide-react';
import { _t } from '@/i18n/t';

export interface SerpSite {
  host: string;
  name: string;
  /** The real homepage title, when the site read got that far. */
  title?: string;
}

interface Props {
  queries: string[];
  site?: SerpSite;
  /** The pass has settled, so no further query is coming. */
  complete?: boolean;
  /** Fired once, when every query this panel knows about has been shown. */
  onPlaybackComplete?: () => void;
}

const QUERY_REVEAL_MS = 3500;
const DONE_VISIBLE = 3;

export default function GoogleChecklistPanel({ queries: allQueries, site, complete = false, onPlaybackComplete }: Props) {
  const [revealCount, setRevealCount] = useState(1);
  useEffect(() => {
    if (revealCount >= allQueries.length) return;
    const id = setTimeout(() => setRevealCount(c => c + 1), QUERY_REVEAL_MS);
    return () => clearTimeout(id);
  }, [revealCount, allQueries.length]);

  const [lastHeld, setLastHeld] = useState(false);
  const lastShown = allQueries.length > 0 && revealCount >= allQueries.length;
  useEffect(() => {
    if (!lastShown) return;
    const id = setTimeout(() => setLastHeld(true), QUERY_REVEAL_MS);
    return () => clearTimeout(id);
  }, [lastShown]);

  const playbackDone = complete && (allQueries.length === 0 || lastHeld);
  const notifiedRef = useRef(false);
  useEffect(() => {
    if (!playbackDone || notifiedRef.current) return;
    notifiedRef.current = true;
    onPlaybackComplete?.();
  }, [playbackDone, onPlaybackComplete]);

  const shown = allQueries.slice(0, Math.min(Math.max(revealCount, 1), allQueries.length));
  const focal = shown[shown.length - 1];
  const done = shown.slice(0, -1).slice(-DONE_VISIBLE);

  return (
    <div className="ob-google" data-testid="onboarding-scan-google">
      {site && <SerpCard site={site} />}

      {focal ? (
        <div className="ob-gfocal text-foreground" key={shown.length} data-testid="onboarding-scan-google-focal">
          {focal}
        </div>
      ) : (
        <p className="ob-gfocal text-muted-foreground">
          {_t('onboarding.crawl.chapter.google.generating')}
        </p>
      )}

      <ul className="ob-gdone">
        {done.map(query => (
          <li key={query} className="text-muted-foreground">
            <Check aria-hidden="true" />
            {query}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * How the site looks in a result, drawn from the scan itself — the real host,
 * the real homepage title. The two bars stand in for a description we have not
 * read yet, which is why they carry no text.
 */
function SerpCard({ site }: { site: SerpSite }) {
  return (
    <div className="ob-serp bg-card" data-testid="onboarding-scan-serp">
      <div className="ob-serp__bar text-muted-foreground">
        <Search aria-hidden="true" />
        <span>{site.host}</span>
      </div>
      <div className="ob-serp__result">
        <span className="ob-serp__fav text-foreground" aria-hidden="true">
          {site.name.trim().charAt(0).toUpperCase()}
        </span>
        <div className="ob-serp__lines">
          <div className="ob-serp__title text-foreground" data-testid="onboarding-scan-serp-title">
            {site.title || site.name}
          </div>
          <div className="ob-serp__url text-muted-foreground">https://{site.host}</div>
          <div className="ob-serp__meta" aria-hidden="true">
            <i style={{ width: '92%' }} />
            <i style={{ width: '64%' }} />
          </div>
        </div>
      </div>
      <span className="ob-serp__beam" aria-hidden="true" />
    </div>
  );
}
